import sortBy from 'lodash-es/sortBy';
import { useMemo } from 'react';
import { FeatureFlagName, GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import {
  PermissionType,
  TeletriageClinicFragment,
  TeletriageStatusFragment,
  useGetTeletriageStatusesQuery,
  useGetTeletriageUserClinicsQuery,
} from 'shared/types/graphql';

interface UseTeletriageClinicDataOutput {
  careClinics: TeletriageClinicFragment[];
  careClinicsIds: string[];
  teletriageStatuses: TeletriageStatusFragment[];
  teletriageStatusNames: string[];
  isLoading: boolean;
}

export const useTeletriageClinicData = (): UseTeletriageClinicDataOutput => {
  const { isFeatureEnabled } = useFeatureFlag();
  const { userHasPermission } = useClinicUser();

  const shouldFetchTeletriageData = useMemo(
    () => userHasPermission(PermissionType.ManageTeletriage) && isFeatureEnabled(FeatureFlagName.CarePlans),
    [isFeatureEnabled, userHasPermission],
  );

  // Care Clinics
  const { data: careClinicsData, loading: loadingCareClinics } = useGetTeletriageUserClinicsQuery({
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    skip: !shouldFetchTeletriageData,
  });

  const careClinics = useMemo(() => {
    const _careClinics = careClinicsData?.getTeletriageUserClinics as TeletriageClinicFragment[];
    const sortedCareClinics = sortBy(_careClinics, 'name');
    return sortedCareClinics || [];
  }, [careClinicsData?.getTeletriageUserClinics]);

  const careClinicsIds = useMemo(() => {
    const filteredClinics = careClinics?.filter((careClinic) => !!careClinic?.id);
    return filteredClinics?.map((careClinic) => careClinic?.id) as string[];
  }, [careClinics]);

  // Teletriage Statuses
  const { data: teletriageStatusesData, loading: loadingTeletriageStatuses } = useGetTeletriageStatusesQuery({
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    skip: !shouldFetchTeletriageData,
  });

  const teletriageStatuses = useMemo(
    () => teletriageStatusesData?.getTeletriageStatuses || [],
    [teletriageStatusesData?.getTeletriageStatuses],
  );

  const teletriageStatusNames = useMemo(() => {
    return teletriageStatuses.map((status) => status.name) || [];
  }, [teletriageStatuses]);

  return {
    careClinics,
    careClinicsIds,
    teletriageStatuses,
    teletriageStatusNames,
    isLoading: loadingCareClinics || loadingTeletriageStatuses,
  };
};
